import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { AppService } from "../../core/services/app.service";

export interface MultipartUploadProgress {
  type: "progress";
  percent: number;
  uploadedParts?: number;
  totalParts?: number;
}

export interface MultipartUploadComplete {
  type: "complete";
  url: string;
}

export type MultipartUploadEvent = MultipartUploadProgress | MultipartUploadComplete;

/** Saved state to resume part uploads (same shape as multi-upload localStorage). */
export interface MultipartResumePayload {
  key: string;
  uploadId: string;
  partsCount: number;
  presignedUrls: string[];
  uploadedParts: Array<{ Etag: string; PartNumber: number }>;
  currentPartIndex: number;
}

export interface MultipartCheckpointPayload {
  uploadedParts: Array<{ Etag: string; PartNumber: number }>;
  currentPartIndex: number;
  presignedUrls: string[];
  key: string;
  uploadId: string;
  partsCount: number;
}

interface MultipartState {
  fileId: string;
  fileName: string;
  file: File;
  uploadId: string;
  key: string;
  partsCount: number;
  uploadedParts: Array<{ Etag: string; PartNumber: number }>;
  presignedUrls: string[];
  currentPartIndex: number;
  folder: string;
  fileType: string;
}

@Injectable({
  providedIn: "root",
})
export class MultipartS3UploadService {
  private static readonly CDN_MEDIA_ORIGIN = "https://i.cameralla.club";

  private readonly MAX_CONCURRENT_UPLOADS = 10;
  private readonly MAX_RETRY = 3;
  private readonly MAX_PARTS = 100;
  private readonly MIN_CHUNK_SIZE_MB = 5;
  private readonly MAX_CHUNK_SIZE_MB = 500;

  constructor(private http: HttpClient) {}

  /**
   * Multipart upload for large videos (same S3 flow as section multi-upload).
   * Emits progress then complete. Teardown aborts in-flight part uploads.
   */
  uploadVideoMultipart(
    folder: string,
    file: File,
    options?: {
      maxSizeMB?: number;
      chunkSizeMB?: number;
      fileId?: string;
      resume?: MultipartResumePayload;
      /** When true, upload is aborted (e.g. user cancelled or component destroying). */
      shouldCancel?: () => boolean;
      /** Persist partial progress for resume (localStorage in multi-upload). */
      onCheckpoint?: (payload: MultipartCheckpointPayload) => void;
    }
  ): Observable<MultipartUploadEvent> {
    const maxSizeMB = options && options.maxSizeMB != null ? options.maxSizeMB : 5120;
    const chunkSizeMB = options && options.chunkSizeMB != null ? options.chunkSizeMB : 15;
    const fileId =
      options && options.fileId != null && options.fileId !== ""
        ? options.fileId
        : `multipart_${Date.now()}_${file.name}`;

    if (!file || !folder) {
      throw new Error("Invalid params");
    }
    if (file.size / 1024 / 1024 >= maxSizeMB) {
      throw new Error("The max size upload is " + maxSizeMB + " MB");
    }

    return new Observable<MultipartUploadEvent>(observer => {
      let cancelled = false;
      const activeXhrs: XMLHttpRequest[] = [];

      const checkCancelled = () => {
        if (cancelled) {
          throw new Error("Upload cancelled");
        }
        if (options && options.shouldCancel && options.shouldCancel()) {
          throw new Error("Upload cancelled");
        }
      };

      const run = async () => {
        try {
          checkCancelled();
          const resume = options && options.resume ? options.resume : null;

          let key: string;
          let uploadId: string;
          let partsCount: number;
          let presignedUrls: string[];

          if (resume && resume.key && resume.uploadId && resume.partsCount) {
            key = resume.key;
            uploadId = resume.uploadId;
            partsCount = resume.partsCount;
            presignedUrls =
              resume.presignedUrls && resume.presignedUrls.length === resume.partsCount
                ? resume.presignedUrls.slice()
                : await this.getPresignedMultipartUrls(key, uploadId, resume.partsCount, checkCancelled);
          } else {
            const initiated = await this.initiateMultipartUpload(folder, file, checkCancelled);
            key = initiated.key;
            uploadId = initiated.uploadId;
            checkCancelled();
            partsCount = this.calculatePartsCount(file.size, chunkSizeMB);
            checkCancelled();
            presignedUrls = await this.getPresignedMultipartUrls(key, uploadId, partsCount, checkCancelled);
          }

          checkCancelled();

          const state: MultipartState = {
            fileId,
            fileName: file.name,
            file,
            uploadId,
            key,
            partsCount,
            uploadedParts: [],
            presignedUrls,
            currentPartIndex: resume ? resume.currentPartIndex : 0,
            folder,
            fileType: file.type || "video/mp4",
          };

          const firstPartNumber =
            resume && resume.uploadedParts && resume.uploadedParts.length > 0 ? resume.currentPartIndex + 1 : 1;

          if (options && options.onCheckpoint) {
            const initialUploaded = resume && resume.uploadedParts ? resume.uploadedParts.slice() : [];
            options.onCheckpoint({
              uploadedParts: initialUploaded,
              currentPartIndex: resume ? resume.currentPartIndex : 0,
              presignedUrls: presignedUrls.slice(),
              key,
              uploadId,
              partsCount,
            });
          }

          await this.uploadAllParts(
            state,
            chunkSizeMB,
            checkCancelled,
            activeXhrs,
            detail => {
              observer.next({
                type: "progress",
                percent: detail.percent,
                uploadedParts: detail.uploadedParts,
                totalParts: detail.totalParts,
              });
            },
            {
              seedParts: resume && resume.uploadedParts ? resume.uploadedParts : [],
              firstPartNumber,
              onCheckpoint: options && options.onCheckpoint ? options.onCheckpoint : undefined,
              keyForCheckpoint: key,
              uploadIdForCheckpoint: uploadId,
            }
          );

          checkCancelled();
          const finalUrl = await this.completeMultipartUpload(key, uploadId, state.uploadedParts, checkCancelled);

          observer.next({ type: "complete", url: finalUrl });
          observer.complete();
        } catch (err) {
          const msg = (err && err.message) || String(err);
          if (cancelled || msg.includes("cancelled") || msg.includes("aborted")) {
            observer.error(new Error("Upload cancelled"));
            return;
          }
          observer.error(err instanceof Error ? err : new Error(msg));
        }
      };

      run();

      return () => {
        cancelled = true;
        activeXhrs.forEach(xhr => {
          if (xhr.readyState !== XMLHttpRequest.DONE) {
            try {
              xhr.abort();
            } catch (_e) {
              /* ignore */
            }
          }
        });
        activeXhrs.length = 0;
      };
    });
  }

  private normalizeMultipartHttpError(error: any): string {
    const networkMessage = "Network error";
    if (!error) {
      return networkMessage;
    }
    if (error instanceof ProgressEvent) {
      return networkMessage;
    }
    if (error.status === 0) {
      return networkMessage;
    }
    const msg = error.message;
    if (typeof msg !== "string" || !msg.trim()) {
      return networkMessage;
    }
    const lower = msg.toLowerCase();
    if (
      msg.includes("function ()") ||
      msg.includes("Http failure") ||
      lower.includes("network") ||
      lower.includes("failed to fetch") ||
      msg === "Unknown Error"
    ) {
      return networkMessage;
    }
    return msg;
  }

  private throwMultipartUploadError(error: any): never {
    if (error instanceof Error) {
      const message = error.message;
      if (
        message === "Invalid response from initiate API" ||
        message.startsWith("Invalid response from") ||
        message.startsWith("Missing presigned URL") ||
        message.startsWith("Invalid response from complete API")
      ) {
        throw error;
      }
    }
    throw new Error(this.normalizeMultipartHttpError(error));
  }

  /** Call S3 multipart complete (e.g. retry after all parts are on S3). */
  completeMultipart(
    key: string,
    uploadId: string,
    parts: Array<{ Etag: string; PartNumber: number }>
  ): Promise<string> {
    const noop = () => {};
    return this.completeMultipartUpload(key, uploadId, parts, noop);
  }

  private async initiateMultipartUpload(
    folder: string,
    file: File,
    checkCancelled: () => void
  ): Promise<{ key: string; uploadId: string }> {
    checkCancelled();
    const fileType = file.type || "video/mp4";
    const url = `${AppService.getApiUrl()}/s3/initiate?folder=${folder}&fileType=${fileType}`;

    try {
      const response: any = await this.http.get(url).toPromise();
      checkCancelled();
      const data = response.Data || response;
      if (!data || !data.key || !data.uploadId) {
        throw new Error("Invalid response from initiate API");
      }
      return { key: data.key, uploadId: data.uploadId };
    } catch (error) {
      this.throwMultipartUploadError(error);
    }
  }

  private async getPresignedMultipartUrls(
    key: string,
    uploadId: string,
    partsCount: number,
    checkCancelled: () => void
  ): Promise<string[]> {
    checkCancelled();
    const url = `${AppService.getApiUrl()}/s3/presignedMultipartUrls?key=${encodeURIComponent(
      key
    )}&uploadId=${encodeURIComponent(uploadId)}&partsCount=${partsCount}`;

    try {
      const response: any = await this.http.get(url).toPromise();
      checkCancelled();
      const presignedUrlsData =
        (response && response.Data && response.Data.presignedUrls) || (response && response.presignedUrls) || response;

      if (!presignedUrlsData || !Array.isArray(presignedUrlsData)) {
        throw new Error("Invalid response from presignedMultipartUrls API");
      }

      const presignedUrlsArray: string[] = new Array(partsCount);
      presignedUrlsData.forEach((item: any, index: number) => {
        if (typeof item === "object" && item && item.partNumber && item.url) {
          const arrayIndex = item.partNumber - 1;
          if (arrayIndex >= 0 && arrayIndex < partsCount) {
            presignedUrlsArray[arrayIndex] = item.url;
          }
        } else if (typeof item === "string" && index < partsCount) {
          presignedUrlsArray[index] = item;
        }
      });

      for (let i = 0; i < partsCount; i++) {
        if (!presignedUrlsArray[i]) {
          throw new Error(`Missing presigned URL for part ${i + 1}`);
        }
      }

      return presignedUrlsArray;
    } catch (error) {
      this.throwMultipartUploadError(error);
    }
  }

  private uploadPartToS3(
    presignedUrl: string,
    chunk: Blob,
    partNumber: number,
    activeXhrs: XMLHttpRequest[],
    checkCancelled: () => void
  ): Promise<string> {
    return new Promise((resolve, reject) => {
      if (!presignedUrl || (presignedUrl.indexOf("http://") !== 0 && presignedUrl.indexOf("https://") !== 0)) {
        reject(new Error(`Invalid presigned URL for part ${partNumber}`));
        return;
      }

      const xhr = new XMLHttpRequest();
      activeXhrs.push(xhr);
      let settled = false;

      const finish = (fn: () => void) => {
        const idx = activeXhrs.indexOf(xhr);
        if (idx > -1) {
          activeXhrs.splice(idx, 1);
        }
        if (!settled) {
          settled = true;
          fn();
        }
      };

      xhr.addEventListener("load", () => {
        finish(() => {
          try {
            checkCancelled();
          } catch (e) {
            reject(e);
            return;
          }
          if (xhr.status >= 200 && xhr.status < 300) {
            let etag = xhr.getResponseHeader("ETag") || xhr.getResponseHeader("etag");
            if (!etag) {
              reject(new Error(`ETag header not found for part ${partNumber}`));
              return;
            }
            const cleanEtag = etag.replace(/"/g, "").trim();
            if (!cleanEtag) {
              reject(new Error(`Invalid ETag for part ${partNumber}`));
              return;
            }
            resolve(cleanEtag);
          } else {
            reject(new Error(`Upload failed for part ${partNumber}. Status: ${xhr.status}`));
          }
        });
      });

      xhr.addEventListener("error", () => {
        finish(() => {
          reject(new Error(`Network error during upload for part ${partNumber}`));
        });
      });

      xhr.addEventListener("abort", () => {
        finish(() => {
          reject(new Error("Upload cancelled"));
        });
      });

      try {
        xhr.open("PUT", presignedUrl);
        xhr.setRequestHeader("Content-Type", "application/octet-stream");
        xhr.send(chunk);
      } catch (e) {
        finish(() => reject(new Error((e && e.message) || "Failed to send part")));
      }
    });
  }

  private async uploadPartWithRetry(
    state: MultipartState,
    partNumber: number,
    chunk: Blob,
    activeXhrs: XMLHttpRequest[],
    checkCancelled: () => void
  ): Promise<string> {
    let attempt = 0;
    let lastError: Error | null = null;

    while (attempt < this.MAX_RETRY) {
      try {
        checkCancelled();
        const partIndex = partNumber - 1;
        let presignedUrl = state.presignedUrls[partIndex];
        if (!presignedUrl) {
          state.presignedUrls = await this.getPresignedMultipartUrls(
            state.key,
            state.uploadId,
            state.partsCount,
            checkCancelled
          );
          presignedUrl = state.presignedUrls[partIndex];
        }
        if (!presignedUrl) {
          throw new Error(`No presigned URL for part ${partNumber}`);
        }

        return await this.uploadPartToS3(presignedUrl, chunk, partNumber, activeXhrs, checkCancelled);
      } catch (error) {
        attempt++;
        lastError = error instanceof Error ? error : new Error(String(error));
        if (attempt >= this.MAX_RETRY) {
          throw new Error(`Failed to upload part ${partNumber} after ${this.MAX_RETRY} attempts: ${lastError.message}`);
        }
        await new Promise(r => setTimeout(r, Math.min(1000 * Math.pow(2, attempt - 1), 5000)));
      }
    }

    throw lastError || new Error(`Failed to upload part ${partNumber}`);
  }

  private async uploadAllParts(
    state: MultipartState,
    chunkSizeMB: number,
    checkCancelled: () => void,
    activeXhrs: XMLHttpRequest[],
    onProgress: (detail: { percent: number; uploadedParts: number; totalParts: number }) => void,
    uploadOpts?: {
      seedParts: Array<{ PartNumber: number; Etag: string }>;
      firstPartNumber: number;
      onCheckpoint?: (payload: MultipartCheckpointPayload) => void;
      keyForCheckpoint: string;
      uploadIdForCheckpoint: string;
    }
  ): Promise<void> {
    const optimalChunkSizeMB = this.calculateOptimalChunkSize(state.file.size, chunkSizeMB);
    const chunkSizeBytes = optimalChunkSizeMB * 1024 * 1024;
    const file = state.file;

    if (optimalChunkSizeMB !== chunkSizeMB) {
      const newPartsCount = Math.ceil(file.size / chunkSizeBytes);
      if (newPartsCount !== state.partsCount) {
        checkCancelled();
        state.partsCount = newPartsCount;
        state.presignedUrls = await this.getPresignedMultipartUrls(
          state.key,
          state.uploadId,
          state.partsCount,
          checkCancelled
        );
      }
    }

    const queue: number[] = [];
    const startPart =
      uploadOpts && uploadOpts.firstPartNumber != null && uploadOpts.firstPartNumber > 0
        ? uploadOpts.firstPartNumber
        : 1;
    for (let p = startPart; p <= state.partsCount; p++) {
      queue.push(p);
    }

    const uploadedPartsMap = new Map<number, { PartNumber: number; Etag: string }>();
    if (uploadOpts && uploadOpts.seedParts && uploadOpts.seedParts.length > 0) {
      uploadOpts.seedParts.forEach(part => {
        uploadedPartsMap.set(part.PartNumber, { PartNumber: part.PartNumber, Etag: part.Etag });
      });
    }

    let hasError = false;
    let errorMessage = "";

    let lastProgressUpdate = 0;
    const PROGRESS_UPDATE_INTERVAL = 200;
    let lastStateSave = 0;
    let lastStateSaveSize = 0;
    const STATE_SAVE_INTERVAL = 5;
    const STATE_SAVE_TIME_INTERVAL = 2000;

    const emitCheckpoint = (mapSize: number): void => {
      if (!uploadOpts || !uploadOpts.onCheckpoint) {
        return;
      }
      const sorted = Array.from(uploadedPartsMap.values()).sort((a, b) => a.PartNumber - b.PartNumber);
      const keys = Array.from(uploadedPartsMap.keys());
      const maxKey = keys.length ? Math.max.apply(null, keys) : 0;
      uploadOpts.onCheckpoint({
        uploadedParts: sorted,
        currentPartIndex: maxKey,
        presignedUrls: state.presignedUrls.slice(),
        key: uploadOpts.keyForCheckpoint,
        uploadId: uploadOpts.uploadIdForCheckpoint,
        partsCount: state.partsCount,
      });
      lastStateSave = Date.now();
      lastStateSaveSize = mapSize;
    };

    const updateStateIfNeeded = (currentSize: number): void => {
      if (!uploadOpts || !uploadOpts.onCheckpoint) {
        return;
      }
      const now = Date.now();
      const shouldSave =
        currentSize - lastStateSaveSize >= STATE_SAVE_INTERVAL ||
        (now - lastStateSave >= STATE_SAVE_TIME_INTERVAL && currentSize > lastStateSaveSize);
      if (shouldSave) {
        emitCheckpoint(currentSize);
      }
    };

    const worker = async (): Promise<void> => {
      while (queue.length > 0 && !hasError) {
        try {
          checkCancelled();
        } catch (_c) {
          hasError = true;
          errorMessage = "Upload cancelled";
          return;
        }

        const partNumber = queue.shift();
        if (!partNumber) {
          return;
        }

        try {
          const start = (partNumber - 1) * chunkSizeBytes;
          const end = Math.min(start + chunkSizeBytes, file.size);
          const chunk = file.slice(start, end);

          const etag = await this.uploadPartWithRetry(state, partNumber, chunk, activeXhrs, checkCancelled);

          uploadedPartsMap.set(partNumber, { PartNumber: partNumber, Etag: etag });
          const currentSize = uploadedPartsMap.size;
          const now = Date.now();
          if (now - lastProgressUpdate >= PROGRESS_UPDATE_INTERVAL || currentSize === state.partsCount) {
            const percent = Math.round((currentSize / state.partsCount) * 100);
            onProgress({
              percent,
              uploadedParts: currentSize,
              totalParts: state.partsCount,
            });
            lastProgressUpdate = now;
          }
          updateStateIfNeeded(currentSize);
        } catch (error) {
          hasError = true;
          errorMessage = (error && error.message) || String(error);
          if (uploadOpts && uploadOpts.onCheckpoint && uploadedPartsMap.size > 0) {
            emitCheckpoint(uploadedPartsMap.size);
          }
          queue.length = 0;
          return;
        }
      }
    };

    await Promise.all(Array.from({ length: this.MAX_CONCURRENT_UPLOADS }, () => worker()));

    if (hasError) {
      throw new Error(errorMessage || "Upload failed");
    }

    state.uploadedParts = Array.from(uploadedPartsMap.values()).sort((a, b) => a.PartNumber - b.PartNumber);

    const broken = state.uploadedParts.filter(p => !p.Etag || !p.Etag.trim());
    if (broken.length) {
      throw new Error(`Some parts are missing ETags: ${broken.map(p => p.PartNumber).join(", ")}`);
    }

    if (uploadOpts && uploadOpts.onCheckpoint) {
      emitCheckpoint(uploadedPartsMap.size);
    }
  }

  /**
   * Prefer `url` from API payload; never return raw S3 hosts — normalize to i.cameralla.club.
   */
  private resolveCompleteMediaUrl(response: any, objectKey: string): string | null {
    const data = response && response.Data;
    const nested = data && data.Data;

    const urlCandidates = [
      data && data.url,
      nested && nested.url,
      response && response.url,
      data && data.cdnUrl,
      nested && nested.cdnUrl,
      response && response.cdnUrl,
      data && typeof data === "string" ? data : null,
      typeof response === "string" ? response : null,
    ];

    for (const candidate of urlCandidates) {
      if (candidate != null && String(candidate).trim() !== "") {
        return this.normalizeUploadMediaUrl(String(candidate));
      }
    }

    const locationCandidates = [response && response.Location, data && data.Location];
    for (const location of locationCandidates) {
      if (location != null && String(location).trim() !== "") {
        return this.normalizeUploadMediaUrl(decodeURIComponent(String(location)));
      }
    }

    const fileKey = (response && response.Key) || objectKey;
    if (fileKey != null && String(fileKey).trim() !== "") {
      return this.normalizeUploadMediaUrl(decodeURIComponent(String(fileKey)));
    }

    return null;
  }

  private normalizeUploadMediaUrl(raw: string): string {
    const value = (raw || "").trim();
    if (!value) {
      return value;
    }

    if (value.startsWith("http://") || value.startsWith("https://")) {
      try {
        const parsed = new URL(value);
        const host = parsed.hostname.toLowerCase();

        if (host === "i.cameralla.club") {
          const path = parsed.pathname.replace(/^\/+/, "");
          const suffix = `${parsed.search || ""}${parsed.hash || ""}`;
          return path
            ? `${MultipartS3UploadService.CDN_MEDIA_ORIGIN}/${path}${suffix}`
            : MultipartS3UploadService.CDN_MEDIA_ORIGIN;
        }

        if (host.includes("amazonaws.com") || host.startsWith("s3.") || host.includes(".s3.")) {
          let objectPath = parsed.pathname.replace(/^\/+/, "");
          const bucketPathPrefix = "i.cameralla.club/";
          if (objectPath.toLowerCase().startsWith(bucketPathPrefix)) {
            objectPath = objectPath.substring(bucketPathPrefix.length);
          }
          const suffix = `${parsed.search || ""}${parsed.hash || ""}`;
          return objectPath
            ? `${MultipartS3UploadService.CDN_MEDIA_ORIGIN}/${objectPath}${suffix}`
            : MultipartS3UploadService.CDN_MEDIA_ORIGIN;
        }
      } catch {
        // fall through — treat as object key
      }
    }

    const objectKey = value.replace(/^\/+/, "");
    return `${MultipartS3UploadService.CDN_MEDIA_ORIGIN}/${objectKey}`;
  }

  private async completeMultipartUpload(
    key: string,
    uploadId: string,
    parts: Array<{ Etag: string; PartNumber: number }>,
    checkCancelled: () => void,
    retryCount = 0
  ): Promise<string> {
    checkCancelled();
    const url = `${AppService.getApiUrl()}/s3/multipart_complete`;
    const body = { key, uploadId, parts };

    try {
      const response: any = await this.http.post(url, body).toPromise();
      checkCancelled();

      const finalUrl = this.resolveCompleteMediaUrl(response, key);

      if (!finalUrl) {
        throw new Error("Invalid response from complete API: No URL found");
      }

      return finalUrl;
    } catch (error) {
      if (retryCount < 2) {
        await new Promise(r => setTimeout(r, 1000 * (retryCount + 1)));
        return this.completeMultipartUpload(key, uploadId, parts, checkCancelled, retryCount + 1);
      }
      this.throwMultipartUploadError(error);
    }
  }

  private calculateOptimalChunkSize(fileSizeBytes: number, chunkSizeMB: number): number {
    const fileSizeMB = fileSizeBytes / (1024 * 1024);
    let optimalChunkSizeMB = chunkSizeMB;
    const partsWithCurrentSize = Math.ceil(fileSizeBytes / (chunkSizeMB * 1024 * 1024));

    if (partsWithCurrentSize > this.MAX_PARTS) {
      optimalChunkSizeMB = Math.ceil((fileSizeMB / this.MAX_PARTS) * 1.1);
      optimalChunkSizeMB = Math.max(this.MIN_CHUNK_SIZE_MB, Math.min(optimalChunkSizeMB, this.MAX_CHUNK_SIZE_MB));
    }

    return optimalChunkSizeMB;
  }

  private calculatePartsCount(fileSize: number, chunkSizeMB: number): number {
    const optimalChunkSizeMB = this.calculateOptimalChunkSize(fileSize, chunkSizeMB);
    const chunkSizeBytes = optimalChunkSizeMB * 1024 * 1024;
    return Math.ceil(fileSize / chunkSizeBytes);
  }
}
